import React, { ReactNode, forwardRef } from "react";

import { ProcedureButton, type ProcedureButtonProps } from "@/features/procedures/ui/components/common/ProcedureButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import {
	CircularProgress,
	useMediaQuery,
	useTheme,
} from "@mui/material";

type SemanticActionButtonProps = Omit<ProcedureButtonProps, "intent"> & {
	children?: ReactNode;
};

export const SaveActionButton = ({
	children = "Save",
	startIcon = <SaveIcon />,
	type = "submit",
	...props
}: SemanticActionButtonProps) => (
	<ProcedureButton
		intent="save"
		type={type}
		startIcon={startIcon}
		{...props}
	>
		{children}
	</ProcedureButton>
);

export const DeleteActionButton = ({
	children = "Delete",
	startIcon = <DeleteIcon />,
	...props
}: SemanticActionButtonProps) => (
	<ProcedureButton intent="danger" startIcon={startIcon} {...props}>
		{children}
	</ProcedureButton>
);

export const CancelActionButton = ({
	children = "Cancel",
	startIcon = <CloseIcon />,
	...props
}: SemanticActionButtonProps) => (
	<ProcedureButton intent="dismiss" startIcon={startIcon} {...props}>
		{children}
	</ProcedureButton>
);

export const CloseActionButton = ({
	children = "Close",
	startIcon = <CloseIcon />,
	...props
}: SemanticActionButtonProps) => (
	<ProcedureButton intent="dismiss" startIcon={startIcon} {...props}>
		{children}
	</ProcedureButton>
);

export const EditActionButton = ({
	children = "Edit",
	startIcon = <EditIcon />,
	...props
}: SemanticActionButtonProps) => (
	<ProcedureButton intent="edit" startIcon={startIcon} {...props}>
		{children}
	</ProcedureButton>
);

export type ActionsMenuButtonProps = Omit<
	ProcedureButtonProps,
	"intent" | "onClick" | "endIcon" | "loading"
> & {
	open: boolean;
	handleClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
	id?: string;
	menuId?: string;
	loading?: boolean;
	children?: ReactNode;
};

export const ActionsMenuButton = forwardRef<
	HTMLButtonElement,
	ActionsMenuButtonProps
>(
	(
		{
			open,
			handleClick,
			id = "app-actions-button",
			menuId = "app-actions-menu",
			loading = false,
			children = "Actions",
			sx,
			disabled,
			...props
		},
		ref,
	) => {
		const theme = useTheme();
		const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

		const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (event) => {
			if (
				event.key === "Enter" ||
				event.key === " " ||
				event.key === "ArrowDown"
			) {
				event.preventDefault();
				handleClick(event as unknown as React.MouseEvent<HTMLButtonElement>);
			}
		};

		return (
			<ProcedureButton
				ref={ref}
				id={id}
				intent="action"
				aria-controls={open ? menuId : undefined}
				aria-haspopup="menu"
				aria-expanded={open ? "true" : undefined}
				fullWidth={isMobile}
				onClick={handleClick}
				onKeyDown={onKeyDown}
				disabled={loading || disabled}
				endIcon={
					loading ? (
						<CircularProgress size={14} color="inherit" />
					) : (
						<KeyboardArrowDownIcon
							fontSize="small"
							sx={{
								transition: "transform 120ms ease",
								transform: open ? "rotate(180deg)" : "rotate(0deg)",
							}}
						/>
					)
				}
				sx={{
					px: 1.5,
					fontSize: "0.875rem",
					lineHeight: 1,
					"& .MuiButton-endIcon": { ml: 0.75 },
					...sx,
				}}
				{...props}
			>
				{children}
			</ProcedureButton>
		);
	},
);

ActionsMenuButton.displayName = "ActionsMenuButton";
